import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Truck } from "lucide-react";
import { homePathForUser } from "@/lib/roleRouting";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Hoş geldiniz!");
      nav(homePathForUser(u));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Giriş başarısız");
    }
    setLoading(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "İstek gönderilemedi");
    }
    setForgotSending(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left panel - branding (Apple-style gradient) */}
      <div className="hidden lg:flex p-12 flex-col justify-between relative overflow-hidden text-foreground border-r border-border/60"
           style={{ background: "linear-gradient(160deg, hsl(var(--card)) 0%, hsl(var(--background)) 100%)" }}>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo.png" alt="AKB" className="h-10 w-auto dark:brightness-0 dark:invert" onError={(e) => e.target.style.display='none'} />
            <div>
              <div className="font-semibold text-[17px] leading-none tracking-tight">AKB KARAVAN</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1.5">International Company</div>
            </div>
          </div>
          <h1 className="text-5xl font-semibold leading-[1.05] tracking-tight mb-5">
            Karavan Üretim<br />
            <span className="text-muted-foreground">Yönetim Sistemi</span>
          </h1>
          <p className="text-muted-foreground text-[15px] leading-relaxed max-w-md">
            Müşteri kabulünden teslimata ve satış sonrası servise kadar tüm üretim akışını araç bazlı yönetin.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-semibold text-2xl tracking-tight">20</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">Modül</div>
          </div>
          <div>
            <div className="font-semibold text-2xl tracking-tight">10</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Departman</div>
          </div>
          <div>
            <div className="font-semibold text-2xl tracking-tight">360°</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">Görünüm</div>
          </div>
        </div>
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1519980744-785dc86d4a7a?w=1200')", backgroundSize: "cover" }}
        />
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md p-8 border-border/60 apple-card animate-apple-fade">
          <div className="mb-8">
            <h2 className="text-3xl font-semibold tracking-tight">Giriş Yap</h2>
            <p className="text-sm text-muted-foreground mt-1.5">Kullanıcı bilgilerinizle sisteme erişin.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Parola</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </Button>
            <button
              type="button"
              onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotDone(false); }}
              className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-4 mt-3 py-1"
              data-testid="forgot-password-link"
            >
              Giriş yapamıyor musunuz? Şifremi Unuttum
            </button>
            <div className="text-[11px] text-center text-muted-foreground mt-1.5">
              Ya da yöneticinize başvurun — size geçici şifre üretebilir.
            </div>
          </form>
        </Card>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(v) => { setForgotOpen(v); if (!v) setForgotDone(false); }}>
        <DialogContent data-testid="forgot-password-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Şifremi Unuttum</DialogTitle>
          </DialogHeader>
          {!forgotDone ? (
            <form onSubmit={submitForgot} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Kayıtlı e-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim. Bağlantı <b>1 saat</b> geçerlidir.
              </p>
              <div>
                <Label htmlFor="forgot-email">E-posta</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-email-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Vazgeç</Button>
                <Button type="submit" disabled={forgotSending} data-testid="forgot-submit-btn">
                  {forgotSending ? "Gönderiliyor…" : "Sıfırlama Bağlantısı Gönder"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-3" data-testid="forgot-success">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                Sıfırlama bağlantısı gönderildi. E-postanızı kontrol edin. Bağlantı 1 saat geçerlidir.
              </div>
              <p className="text-xs text-slate-500">
                E-posta gelmediyse spam/gereksiz klasörünü kontrol edin. Doğru adresi yazdığınızdan emin olun — güvenlik için sistem, e-posta kayıtlı olsa da olmasa da aynı yanıtı verir.
              </p>
              <DialogFooter>
                <Button onClick={() => setForgotOpen(false)}>Tamam</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
